import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';


const SyncStatusCard = ({ provider }) => {
  const getHealthStyles = (health) => {
    switch (health) {
      case 'healthy':
        return {
          bg: 'bg-success/10',
          text: 'text-success',
          icon: 'CheckCircle',
          badge: 'bg-success text-success-foreground'
        };
      case 'warning':
        return {
          bg: 'bg-warning/10',
          text: 'text-warning',
          icon: 'AlertTriangle',
          badge: 'bg-warning text-warning-foreground'
        };
      case 'error':
        return {
          bg: 'bg-error/10',
          text: 'text-error',
          icon: 'XCircle',
          badge: 'bg-error text-error-foreground'
        };
      case 'inactive':
        return {
          bg: 'bg-muted',
          text: 'text-muted-foreground',
          icon: 'MinusCircle',
          badge: 'bg-muted text-muted-foreground'
        };
      default:
        return {
          bg: 'bg-muted',
          text: 'text-muted-foreground',
          icon: 'HelpCircle',
          badge: 'bg-muted text-muted-foreground'
        };
    }
  };

  const getProviderDisplayName = (name) => {
    const names = {
      'able': 'ABLE Payroll',
      'smoothpay': 'Smoothpay',
      'bsp_quick_pay': 'BSP Quick Pay',
      'alesco': 'ALESCO'
    };
    return names?.[name] || name;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatNextSync = (timestamp) => {
    if (!timestamp) return 'Not scheduled';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = date - now;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMs < 0) return 'Overdue';
    if (diffMins < 60) return `In ${diffMins}m`;
    if (diffHours < 24) return `In ${diffHours}h`;
    return `In ${diffDays}d`;
  };

  const styles = getHealthStyles(provider?.connectionHealth);

  return (
    <div className={`${styles?.bg} border border-current/20 rounded-lg p-4 transition-smooth hover:shadow-md`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon name="Database" size={20} color="currentColor" className={styles?.text} />
          <h3 className="font-heading font-semibold text-base text-foreground">
            {getProviderDisplayName(provider?.providerName)}
          </h3>
        </div>
        <span className={`${styles?.badge} px-2 py-1 text-xs font-caption rounded-md flex items-center gap-1`}>
          <Icon name={styles?.icon} size={12} color="currentColor" />
          {provider?.connectionHealth?.toUpperCase()}
        </span>
      </div>

      <div className="space-y-2 mb-3">
        <div className="flex items-center justify-between text-sm">
          <span className="caption text-muted-foreground">Status:</span>
          <span className="data-text font-medium text-foreground capitalize">
            {provider?.integrationStatus?.replace('_', ' ')}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="caption text-muted-foreground">Last Sync:</span>
          <span className="data-text font-medium text-foreground">
            {formatTimestamp(provider?.lastSyncAt)}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="caption text-muted-foreground">Next Sync:</span>
          <span className="data-text font-medium text-foreground">
            {formatNextSync(provider?.nextSyncAt)}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="caption text-muted-foreground">Frequency:</span>
          <span className="data-text font-medium text-foreground capitalize">
            {provider?.syncFrequency || 'Manual'}
          </span>
        </div>
      </div>

      {provider?.unresolvedConflicts > 0 && (
        <div className="bg-error/10 border border-error/20 rounded-md p-2 mb-3">
          <div className="flex items-center gap-2">
            <Icon name="AlertTriangle" size={14} color="var(--color-error)" />
            <span className="text-xs font-medium text-error">
              {provider?.unresolvedConflicts} Unresolved Conflict{provider?.unresolvedConflicts !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <div className="bg-muted/50 rounded-md p-2">
          <div className="flex items-center gap-1 mb-1">
            <Icon name="AlertCircle" size={12} color="var(--color-muted-foreground)" />
            <span className="text-xs caption text-muted-foreground">Total Conflicts</span>
          </div>
          <span className="text-lg font-bold data-text text-foreground">
            {provider?.totalConflicts || 0}
          </span>
        </div>
        <div className="bg-muted/50 rounded-md p-2">
          <div className="flex items-center gap-1 mb-1">
            <Icon name="Activity" size={12} color="var(--color-muted-foreground)" />
            <span className="text-xs caption text-muted-foreground">Active</span>
          </div>
          <span className="text-lg font-bold data-text text-foreground">
            {provider?.isActive ? 'Yes' : 'No'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default SyncStatusCard;